import { Sparkles } from 'lucide-react';

import { useLanguage } from '@/components/LanguageContext';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import HeroProductoV3 from '@/components/landing/v3/HeroProductoV3';
import PorQueImporta from '@/components/landing/v3/PorQueImporta';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * PublicacionesIaV3 — crear y programar publicaciones en la ficha de Google.
 *
 * Portada desde `Publicaciones IA.dc.html`.
 *
 * TRES COSAS QUE NO SON COMO EN EL DISEÑO:
 *
 * 1. **Sin la repetición semanal/mensual.** El diseño la anuncia en el cuerpo y
 *    en una FAQ, y NO EXISTE: un programado tiene un `scheduled_at` y un
 *    `status` que va de `pending` a `published`, una vez. Ver la cabecera de
 *    `landingV3.producto.js`.
 * 2. **El «lo que escribes tú» es maqueta, no una captura.** El original pega
 *    un PNG de nuestro propio formulario (`pasted-1789983561201-0.png`). Una
 *    captura de una UI no se traduce, no escala en móvil y queda vieja en
 *    cuanto alguien mueve un campo; esto es el mismo formulario en marcado.
 * 3. ~~**La foto del ejemplo es un HUECO.**~~ Ya no: la foto definitiva llegó y
 *    está en `FotoPublicacion`. Del original de 2560×2560 y 558 KB salió un
 *    WebP de 960×600 y 55 KB, recortado al 16:10 que pide el maquetado — servir
 *    medio mega para un hueco de 380 px es lo que hunde Core Web Vitals.
 */
export default function PublicacionesIaV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.producto.publicacionesIa;
  const comun = raiz.producto.comun;

  return (
    <LandingShellV3>
      <HeroProductoV3
        badge={c.hero.badge}
        titulo={c.hero.titulo}
        acento={c.hero.acento}
        tituloB={c.hero.tituloB}
        sub={c.hero.sub}
        cta={c.hero.cta}
        ctaVer={c.hero.ctaVer}
        anclaVer="#ejemplo"
        ctaTrialId="producto.publicaciones.hero.trial"
        ctaVerId="producto.publicaciones.hero.ver"
      >
        <PreviewPublicacion c={c.hero.preview} />
      </HeroProductoV3>

      <Problema c={c.problema} />
      <Crear c={c.crear} />
      <TiposYProgramar tipos={c.tipos} programar={c.programar} />
      <Multi c={c.multi} />

      <PorQueImporta
        kicker={c.porQue.kicker}
        titulo={c.porQue.titulo}
        parrafos={[c.porQue.p1, c.porQue.p2]}
      />

      <FaqAcordeon titulo={c.faqTitulo} preguntas={c.faq} />

      <CtaFinalOscuro
        tituloA={c.cierre.titulo}
        acento={c.cierre.acento}
        subtitulo={c.cierre.sub}
        cta={comun.cierre.cta}
        microcopy={comun.cierre.microcopy}
        enterpriseA={comun.cierre.enterpriseA}
        enterpriseCta={comun.cierre.enterpriseCta}
        ctaTrial="producto.publicaciones.cierre.trial"
        ctaVentas="producto.publicaciones.cierre.ventas"
      />
    </LandingShellV3>
  );
}

/**
 * La foto de la publicación de ejemplo.
 *
 * Estuvo un tiempo siendo un hueco gris con un icono, a la espera de foto
 * definitiva. Ya la hay.
 *
 * ── POR QUÉ `alt=""` ────────────────────────────────────────────────────────
 *
 * Porque justo debajo va el texto de la publicación, que describe la oferta con
 * todo detalle («2x1 en nuestra hamburguesa estrella, doble carne de Angus…»).
 * Un `alt` descriptivo le haría oír lo mismo dos veces a quien usa un lector de
 * pantalla. Vacío es lo correcto aquí, no un atajo: la imagen ilustra un texto
 * que ya está.
 *
 * ── LAS DOS COSAS QUE NO SE VEN ─────────────────────────────────────────────
 *
 * `width`/`height` van puestos aunque el tamaño real lo decida el CSS: son los
 * que permiten al navegador reservar el hueco antes de descargarla, y sin ellos
 * la tarjeta salta cuando la foto entra.
 *
 * Y `loading` es un parámetro y no un valor fijo: la del hero está en el primer
 * pantallazo y ponerle `lazy` RETRASA su carga en vez de adelantarla; la de más
 * abajo sí debe ser diferida.
 */
const FOTO_EJEMPLO = '/landing-images/v3/publicacion-ejemplo.webp';

function FotoPublicacion({ className = '', prioritaria = false }) {
  return (
    <img
      src={FOTO_EJEMPLO}
      alt=""
      width={960}
      height={600}
      loading={prioritaria ? 'eager' : 'lazy'}
      decoding="async"
      className={`object-cover ${className}`}
    />
  );
}

function PreviewPublicacion({ c }) {
  return (
    <div className="bg-white border border-v3-line rounded-3xl v3-shadow overflow-hidden max-w-[380px] mx-auto">
      <div className="px-[18px] py-3.5 flex items-center justify-between gap-2.5 border-b border-black/5">
        <span className="text-[12.5px] font-semibold text-v3-muted">{c.etiqueta}</span>
        <span className="text-[11.5px] font-semibold bg-v3-goldsoft text-v3-golddeep rounded-full px-2.5 py-1.5">
          {c.tipo}
        </span>
      </div>
      <FotoPublicacion className="w-full aspect-[16/10]" prioritaria />
      <div className="p-[18px]">
        <p className="text-[14.5px] leading-[1.6] text-v3-ink">{c.texto}</p>
        <div className="mt-4 pt-4 border-t border-black/5">
          {/* El botón de una publicación de Google: es parte de la maqueta, no
              un control de esta página. */}
          <span
            aria-hidden="true"
            className="inline-block text-[13.5px] font-semibold text-[#1a73e8] border border-[#dadce0] rounded-full px-5 py-2.5"
          >
            {c.boton}
          </span>
        </div>
      </div>
    </div>
  );
}

function Problema({ c }) {
  return (
    <section className="px-6 pt-[60px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10">
        <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
          {c.titulo}
        </h2>
        <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[70ch]">{c.p1}</p>
        <p className="text-base leading-[1.7] text-v3-body mt-3.5 max-w-[70ch]">{c.p2}</p>
      </div>
    </section>
  );
}

function Crear({ c }) {
  const p = c.prompt;

  return (
    <section id="ejemplo" className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="mt-7 mx-auto max-w-[860px] text-center">
          <h2 className="text-[27px] md:text-[32px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink mx-auto max-w-[22ch]">
            {c.titulo}
          </h2>
          <p className="text-[16.5px] leading-[1.65] text-v3-body mt-4 mx-auto max-w-[62ch]">
            {c.cuerpo}
          </p>
        </div>

        <div className="bg-white border border-v3-line rounded-[20px] v3-shadow mt-7 p-[18px] grid gap-[18px] lg:grid-cols-2">
          <div>
            <div className="text-sm font-bold tracking-[-.01em] px-1 pb-3 text-v3-ink">
              {c.tuLado}
            </div>
            {/* El formulario, en marcado. Todo `aria-hidden`: es una ilustración
                de la UI, no un formulario que se pueda rellenar aquí. */}
            <div
              aria-hidden="true"
              className="border border-v3-line rounded-2xl p-4 bg-v3-surface flex flex-col gap-3"
            >
              <div>
                <div className="text-[11.5px] font-bold text-v3-faint">{p.etiqueta}</div>
                <div className="bg-white border border-v3-line rounded-xl px-3.5 py-3 mt-1.5 text-[14.5px] text-v3-ink">
                  {p.texto}
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] font-bold text-v3-faint">{p.tipoEtiqueta}</div>
                  <div className="bg-white border border-v3-line rounded-xl px-3.5 py-2.5 mt-1.5 text-[13.5px] font-semibold text-v3-ink truncate">
                    {p.tipo}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] font-bold text-v3-faint">{p.botonEtiqueta}</div>
                  <div className="bg-white border border-v3-line rounded-xl px-3.5 py-2.5 mt-1.5 text-[13.5px] font-semibold text-v3-ink truncate">
                    {p.boton}
                  </div>
                </div>
              </div>
              <div className="bg-v3-gold text-v3-ink text-sm font-bold rounded-full py-3 text-center inline-flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                {p.generar}
              </div>
            </div>
          </div>

          <div>
            <div className="text-sm font-bold tracking-[-.01em] px-1 pb-3 text-v3-ink">
              {c.suLado}
            </div>
            <div className="border border-v3-line rounded-2xl overflow-hidden">
              <FotoPublicacion className="w-full aspect-[16/10]" />
              <div className="p-4">
                <p className="text-[14.5px] leading-[1.6] text-v3-ink">{c.salida.texto}</p>
                <div className="mt-3.5 pt-3.5 border-t border-black/5">
                  <span
                    aria-hidden="true"
                    className="inline-block text-[13.5px] font-semibold text-[#1a73e8] border border-[#dadce0] rounded-full px-5 py-2.5"
                  >
                    {c.salida.boton}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TiposYProgramar({ tipos, programar }) {
  // 28 días con seis publicaciones repartidas, como el calendario del diseño.
  const marcados = { 3: '#1a73e8', 8: '#feae2c', 11: '#1a73e8', 17: '#7c3aed', 22: '#feae2c', 25: '#1a73e8' };
  const tonos = { bien: 'bg-v3-greensoft text-v3-green', medio: 'bg-v3-goldsoft text-v3-golddeep' };

  return (
    <section className="px-6 pt-6">
      <div className="max-w-[1180px] mx-auto grid gap-6 lg:grid-cols-2">
        <article className="bg-white border border-v3-line rounded-[20px] px-7 py-[30px]">
          <h2 className="text-[22px] md:text-2xl leading-[1.2] tracking-[-.02em] font-bold text-v3-ink max-w-[22ch]">
            {tipos.titulo}
          </h2>
          <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3">{tipos.cuerpo}</p>

          <ul className="flex flex-col gap-2 mt-[22px]">
            {tipos.lista.map((t) => (
              <li
                key={t.clave}
                className="bg-v3-surface rounded-xl px-3.5 py-3 flex items-center gap-3"
              >
                <span
                  aria-hidden="true"
                  className="w-[9px] h-[9px] rounded-full flex-none"
                  style={{ background: t.color }}
                />
                <span className="flex-1 text-sm font-semibold tracking-[-.01em] text-v3-ink">
                  {t.n}
                </span>
                <span className="text-xs text-v3-muted">{t.d}</span>
              </li>
            ))}
          </ul>

          <div className="text-[11.5px] font-bold text-v3-faint mt-5">{tipos.botonesEtiqueta}</div>
          <ul className="flex gap-2 flex-wrap mt-2.5">
            {tipos.botones.map((b) => (
              <li
                key={b}
                className="bg-v3-sunken text-v3-body text-[13px] font-medium rounded-full px-3.5 py-2"
              >
                {b}
              </li>
            ))}
          </ul>
        </article>

        <article className="bg-white border border-v3-line rounded-[20px] px-7 py-[30px]">
          <h2 className="text-[22px] md:text-2xl leading-[1.2] tracking-[-.02em] font-bold text-v3-ink max-w-[22ch]">
            {programar.titulo}
          </h2>
          <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3">{programar.cuerpo}</p>

          <div className="bg-v3-surface rounded-2xl p-[18px] mt-[22px]" aria-hidden="true">
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {programar.diasSemana.map((d, i) => (
                <span key={i} className="text-center text-[10.5px] text-v3-faint font-medium">
                  {d}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 28 }, (_, i) => {
                const color = marcados[i];
                return (
                  <span
                    key={i}
                    className={`aspect-square rounded-full flex items-center justify-center text-[11.5px] ${
                      color ? 'font-bold text-white' : 'font-medium text-v3-faint bg-v3-track'
                    }`}
                    style={color ? { background: color } : undefined}
                  >
                    {i + 1}
                  </span>
                );
              })}
            </div>
            <div className="flex gap-3.5 flex-wrap mt-4 pt-3.5 border-t border-black/5">
              {tipos.lista.map((t) => (
                <span key={t.clave} className="flex items-center gap-2 text-[11.5px] text-v3-body">
                  <span
                    className="w-[9px] h-[9px] rounded-full block"
                    style={{ background: t.color }}
                  />
                  {t.n}
                </span>
              ))}
            </div>
          </div>

          <ul className="flex flex-col gap-2 mt-3.5">
            {programar.recientes.map((r) => (
              <li
                key={r.clave}
                className="bg-v3-surface rounded-xl px-3.5 py-3 flex items-center gap-3"
              >
                <span
                  aria-hidden="true"
                  className="w-[9px] h-[9px] rounded-full flex-none"
                  style={{ background: r.color }}
                />
                <span className="flex-1 min-w-0 text-[13.5px] font-semibold tracking-[-.01em] text-v3-ink truncate">
                  {r.t}
                </span>
                <span
                  className={`flex-none text-[11.5px] font-semibold rounded-full px-2.5 py-1.5 whitespace-nowrap ${tonos[r.tono]}`}
                >
                  {r.estado}
                </span>
              </li>
            ))}
          </ul>
        </article>
      </div>
    </section>
  );
}

function Multi({ c }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-11 items-center mt-7">
          <div>
            <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[24ch]">
              {c.titulo}
            </h2>
            <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[60ch]">{c.cuerpo}</p>
          </div>

          <div className="bg-v3-surface rounded-[20px] p-6">
            <div className="text-[12.5px] font-bold text-v3-muted">{c.variablesEtiqueta}</div>
            <dl className="flex flex-col gap-2 mt-3.5">
              {c.variables.map((v) => (
                <div
                  key={v.k}
                  className="bg-white rounded-xl px-3.5 py-3 flex items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,.06)]"
                >
                  <dt className="font-mono text-[12.5px] font-semibold text-v3-golddeep bg-v3-goldsoft rounded-lg px-2.5 py-1.5 flex-none">
                    {v.k}
                  </dt>
                  <dd className="flex-1 text-[13.5px] text-v3-body">{v.d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
